/**
 * Typed table renderer for the `table` structural intent.
 *
 * Parses a single markdown-table source block via marked's lexer and
 * renders a real `<table class="answer-table">` so we can style header
 * vs. body, alignment, and zebra rows from CSS tokens instead of from
 * marked's stringified HTML.
 */
import DOMPurify from "dompurify";
import { Lexer, Marked, type Tokens } from "marked";
import { type Component, For, createMemo } from "solid-js";

const inlineMarked = new Marked({ gfm: true, breaks: true });

function renderInline(raw: string): string {
  const html = inlineMarked.parseInline(raw, { async: false }) as string;
  return DOMPurify.sanitize(html, { ADD_ATTR: ["target", "rel"] });
}

function alignStyle(
  align: "left" | "center" | "right" | null | undefined,
): { "text-align": "left" | "center" | "right" } | undefined {
  if (align === "left" || align === "center" || align === "right") {
    return { "text-align": align };
  }
  return undefined;
}

function findTable(source: string): Tokens.Table | null {
  const tokens = Lexer.lex(source);
  for (const t of tokens) {
    if (t.type === "table") return t as Tokens.Table;
  }
  return null;
}

interface Props {
  source: string;
}

const AnswerTable: Component<Props> = (props) => {
  const table = createMemo(() => findTable(props.source));

  return (
    <div class="answer-table-wrap">
      {(() => {
        const t = table();
        if (!t) return null;
        return (
          <table class="answer-table">
            <thead>
              <tr>
                <For each={t.header}>
                  {(cell, i) => (
                    <th
                      style={alignStyle(t.align[i()])}
                      // eslint-disable-next-line solid/no-innerhtml
                      innerHTML={renderInline(cell.text)}
                    />
                  )}
                </For>
              </tr>
            </thead>
            <tbody>
              <For each={t.rows}>
                {(row) => (
                  <tr>
                    <For each={row}>
                      {(cell, i) => (
                        <td
                          style={alignStyle(t.align[i()])}
                          // eslint-disable-next-line solid/no-innerhtml
                          innerHTML={renderInline(cell.text)}
                        />
                      )}
                    </For>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        );
      })()}
    </div>
  );
};

export default AnswerTable;
